import { classMerge } from '@components/utilities/classMerge'
import React from 'react'

interface CircularProgressRingProps {
  backgroundCircleClassName?: string
  children?: React.ReactNode
  className?: string
  goal: number | undefined
  progress: number
  progressCircleClassName?: string
  size?: number
  strokeWidth?: number
}

export default function CircularProgressRing({
  backgroundCircleClassName,
  children,
  className,
  goal = 100,
  progress,
  progressCircleClassName,
  size = 48,
  strokeWidth = 4,
}: CircularProgressRingProps) {
  // Normalize progress value between 0 and goal
  const normalizedProgress = Math.min(Math.max(progress, 0), goal)

  // Calculate the radius of the circle (outer radius minus half the stroke width)
  const radius = (size - strokeWidth) / 2

  // Calculate the circumference of the circle
  const circumference = 2 * Math.PI * radius

  // Calculate the stroke-dashoffset based on the progress
  const dashOffset = circumference - (normalizedProgress / goal) * circumference

  // Center position for the circle
  const center = size / 2

  return (
    <div
      className={classMerge(
        'relative inline-flex items-center justify-center',
        className,
      )}
    >
      <svg className='-rotate-90 transform' height={size} width={size}>
        {/* Background Circle */}
        <circle
          className={classMerge(
            'fill-transparent stroke-primary100',
            backgroundCircleClassName,
          )}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
        />

        {/* Progress Circle */}
        <circle
          className={classMerge(
            'fill-transparent stroke-primary400 transition-[stroke-dashoffset] duration-300 ease-in-out',
            progressCircleClassName,
          )}
          cx={center}
          cy={center}
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap='round'
          strokeWidth={strokeWidth}
        />
      </svg>

      {/* Center Content */}
      {children && (
        <div className='absolute inset-0 flex items-center justify-center'>
          {children}
        </div>
      )}
    </div>
  )
}
